import { useEffect } from "react";

export const SITE_URL = "https://ascendiadetail.com";
const DEFAULT_IMAGE = "https://ascendiadetail.com/og-image.jpg";

function upsertMeta(selector, attrs) {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => k !== "content" && tag.setAttribute(k, v));
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", attrs.content);
}

function upsertLink(rel, href) {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

/**
 * SEO / head manager. Sets title, description, canonical, robots, Open Graph,
 * Twitter tags and (optionally) JSON-LD structured data. All tags are written to
 * the document head so they are captured by the build-time prerenderer and by
 * JavaScript-rendering crawlers alike.
 *
 * Props:
 *  - title, description: strings
 *  - path: canonical path (defaults to current pathname)
 *  - image: absolute OG/Twitter image URL
 *  - type: og:type (default "website")
 *  - noindex: boolean -> emits "noindex, nofollow"
 *  - jsonLd: object | array of schema.org objects
 */
export const SEO = ({ title, description, path, image, type = "website", noindex = false, jsonLd }) => {
  const fullTitle = title ? `${title} | Ascendia Detail` : "Ascendia Detail | Premium Property Detailing & Cleaning in Israel";

  useEffect(() => {
    if (typeof document === "undefined") return;

    document.title = fullTitle;

    const canonicalPath = path || (typeof window !== "undefined" ? window.location.pathname : "/");
    const canonicalUrl = `${SITE_URL}${canonicalPath}`.replace(/\/+$/, canonicalPath === "/" ? "/" : "");
    const img = image || DEFAULT_IMAGE;

    if (description) upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large",
    });
    upsertLink("canonical", canonicalUrl);

    // Open Graph
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "Ascendia Detail" });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    if (description) upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: img });
    upsertMeta('meta[property="og:image:secure_url"]', { property: "og:image:secure_url", content: img });
    upsertMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: title || "Ascendia Detail — Premium Property Detailing" });
    // Only assert fixed dimensions/type for our dedicated 1200x630 card.
    const isDefault = img === DEFAULT_IMAGE;
    upsertMeta('meta[property="og:image:type"]', { property: "og:image:type", content: isDefault ? "image/jpeg" : "" });
    upsertMeta('meta[property="og:image:width"]', { property: "og:image:width", content: isDefault ? "1200" : "" });
    upsertMeta('meta[property="og:image:height"]', { property: "og:image:height", content: isDefault ? "630" : "" });

    // Twitter
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    if (description) upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: img });
    upsertMeta('meta[name="twitter:image:alt"]', { name: "twitter:image:alt", content: title || "Ascendia Detail — Premium Property Detailing" });

    // JSON-LD structured data (page-specific). Tagged so we can clean up on unmount.
    let scriptEl;
    if (jsonLd) {
      scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.setAttribute("data-seo-jsonld", "page");
      scriptEl.text = JSON.stringify(jsonLd);
      document.head.appendChild(scriptEl);
    }
    return () => {
      if (scriptEl && scriptEl.parentNode) scriptEl.parentNode.removeChild(scriptEl);
    };
  }, [fullTitle, description, path, image, type, noindex, jsonLd]);

  return null;
};

export default SEO;
